import type { Message } from '../types/message';
import baseApi from './baseApi';

export interface WidgetTheme {
  primaryColor?: string | null;
  onPrimaryColor?: string | null;
  bubbleColor?: string | null;
  onBubbleColor?: string | null;
  headerBackground?: string | null;
  headerTextColor?: string | null;
  chatBackground?: string | null;
  onlineDotColor?: string | null;
  fontFamily?: string | null;
  launcherIconUrl?: string | null;
}

export interface WidgetCopy {
  welcomeTitle?: string | null;
  welcomeBody?: string | null;
  startButtonLabel?: string | null;
  inputPlaceholder?: string | null;
  greeting?: string | null;
  suggestedQuestions?: string[] | null;
}

export interface WidgetConfig {
  displayName: string;
  subtitle: string | null;
  avatarUrl: string | null;
  theme: WidgetTheme | null;
  copy: WidgetCopy | null;
}

const publicAccessApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    createDemochatClient: build.mutation({
      query: (props: {
        companyIdentifier: string;
        recaptchaToken: string;
        isFromWidget?: boolean;
        source?: string;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
        clientData?: {
          firstName: string;
          lastName: string;
          rut?: string;
          dni?: string;
          email: string;
          modelOfInterest: string;
          brandOfInterest: string;
          source: string;
        };
      }) => ({
        url: '/demochat/clients',
        method: 'POST',
        body: {
          companyIdentifier: props.companyIdentifier,
          recaptchaToken: props.recaptchaToken,
          isFromWidget: props.isFromWidget,
          source: props.source,
          utmSource: props.utmSource,
          utmMedium: props.utmMedium,
          utmCampaign: props.utmCampaign,
          clientData: props.clientData,
        },
      }),
      transformResponse: (response: { id: string }) => response,
    }),
    getDemoClientMessages: build.query({
      query: (props: { clientPhoneIdentifier: string }) => ({
        url: `/demochat/messages?clientPhoneIdentifier=${props.clientPhoneIdentifier}`,
        method: 'GET',
      }),
      transformResponse: (response: Message[]) => response,
    }),
    createDemochatMessage: build.mutation({
      query: (props: { clientPhoneIdentifier: string; message: string }) => ({
        url: '/demochat/messages',
        method: 'POST',
        body: props,
      }),
      transformResponse: (response: { status: string }) => response,
    }),
    getIsPrivateDemo: build.query({
      query: (props: { companyIdentifier: string }) => ({
        url: `/demochat/is-private-demo?companyIdentifier=${props.companyIdentifier}`,
        method: 'GET',
      }),
      transformResponse: (response: { isPrivateDemo: boolean }) => response,
    }),
    getWidgetConfig: build.query({
      query: (props: { companyIdentifier: string }) => ({
        url: `/demochat/widget-config?companyIdentifier=${props.companyIdentifier}`,
        method: 'GET',
      }),
      transformResponse: (response: WidgetConfig) => response,
    }),
    createDemochatMessageWithFiles: build.mutation({
      query: (props: { clientPhoneIdentifier: string; files: File[]; message?: string }) => {
        const formData = new FormData();
        formData.append('clientPhoneIdentifier', props.clientPhoneIdentifier);
        props.files.forEach((file) => {
          formData.append('files', file);
        });
        if (props.message) {
          formData.append('message', props.message);
        }
        return {
          url: '/demochat/message-with-files',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { status: string }) => response,
    }),
    generateChatDescription: build.mutation({
      query: (props: { clientPhoneIdentifier: string }) => ({
        url: '/demochat/generate-description',
        method: 'POST',
        body: {
          clientPhoneIdentifier: props.clientPhoneIdentifier,
        },
      }),
      transformResponse: (response: { description: string }) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDemoClientMessagesQuery,
  useCreateDemochatClientMutation,
  useCreateDemochatMessageMutation,
  useGetIsPrivateDemoQuery,
  useGetWidgetConfigQuery,
  useCreateDemochatMessageWithFilesMutation,
  useGenerateChatDescriptionMutation,
} = publicAccessApi;
